// ============================================================
// Platform Control · SSO — enterprise identity federation.
// ============================================================

import { useNavigate } from 'react-router-dom'
import ViewShell, { Card, Row, Section, ActionButton, ActionRow } from './_ViewShell'
import EmptyState from '../../../components/EmptyState'
import { SHELL_TOKENS } from '../../../shell/styles'

const SUPPORTED_FUTURE = [
  { id: 'saml',      label: 'SAML 2.0',        note: 'IdP-initiated and SP-initiated' },
  { id: 'oidc',      label: 'OpenID Connect',  note: 'Auth code + PKCE' },
  { id: 'google',    label: 'Google Workspace', note: 'OIDC provider' },
  { id: 'microsoft', label: 'Microsoft Entra',  note: 'SAML or OIDC' },
]

export default function SSOView() {
  const navigate = useNavigate()

  return (
    <ViewShell
      kicker="PLATFORM CONTROL · SSO"
      title="Single Sign-On"
      description="Enterprise identity federation status."
    >
      <Section label="CURRENT STATUS">
        <Card>
          <div style={{ padding: '16px 0 12px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              padding: '4px 10px', borderRadius: 6,
              background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
              fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted,
              textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
            }}>
              Not active
            </div>
            <span style={{ fontSize: 12, color: SHELL_TOKENS.inkMuted }}>
              Enterprise SSO requires backend activation
            </span>
          </div>
        </Card>
      </Section>

      <Section label="ACTIVE AUTH PATHS">
        <Card>
          <Row label="Email/password" value="Active" hint="hardened via POST /api/auth/login" />
          <Row label="GitHub OAuth" value="Config-blocked" hint="requires Supabase Dashboard provider setup" />
          <ActionRow>
            <ActionButton onClick={() => navigate('/square/control/oauth')}>
              Manage OAuth Apps
            </ActionButton>
            <ActionButton onClick={() => navigate('/auth/login')}>
              Test Login
            </ActionButton>
            <ActionButton onClick={() => window.open('https://supabase.com/dashboard', '_blank')}>
              Open Supabase Auth Settings
            </ActionButton>
          </ActionRow>
        </Card>
      </Section>

      <Section label="DISTINCTION">
        <Card>
          <div style={{ fontSize: 11, color: SHELL_TOKENS.inkMuted, lineHeight: 1.6, padding: '6px 0' }}>
            <strong style={{ color: SHELL_TOKENS.ink }}>GitHub login is social authentication</strong>, not enterprise SSO.
            It authenticates individual users via their GitHub account. Enterprise SSO (SAML/OIDC)
            delegates authentication to an organization's identity provider. These are separate concerns.
          </div>
        </Card>
      </Section>

      <Section label="FUTURE ENTERPRISE PROVIDERS">
        <Card>
          {SUPPORTED_FUTURE.map((p, i) => (
            <div key={p.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '10px 0',
              borderBottom: i === SUPPORTED_FUTURE.length - 1 ? 'none' : `1px solid ${SHELL_TOKENS.border}`,
            }}>
              <div style={{ fontSize: 12, color: SHELL_TOKENS.ink, fontWeight: 600 }}>{p.label}</div>
              <div style={{ fontSize: 10, color: SHELL_TOKENS.inkFaint, fontFamily: 'var(--font-mono)' }}>{p.note}</div>
            </div>
          ))}
        </Card>
      </Section>
    </ViewShell>
  )
}
